import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AdminCarriersPage = () => {
  const { hasPermission } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [carrierBackends, setCarrierBackends] = useState({});
  const [backends, setBackends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [editingCarrierName, setEditingCarrierName] = useState('');
  const [showCreateCarrierModal, setShowCreateCarrierModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [userTypes, setUserTypes] = useState([]);
  const [newCarrierData, setNewCarrierData] = useState({
    carrier_name: '',
    backend_id: '',
    is_active: 1
  });
  const [createUserData, setCreateUserData] = useState({
    carrier_id: '',
    user_type_id: '',
    auth_method: 'google_oauth',
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    username: '',
    password: '',
    oauth_provider_id: ''
  });

  // Get API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

 // Memoized function to fetch carriers
 const fetchCarriers = useCallback(async () => {
   try {
     setLoading(true);
     const token = localStorage.getItem('authToken');
     const response = await axios.get(`${API_BASE_URL}/admin/carriers`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setCarriers(response.data.carriers || []);
     
     // Fetch backend associations for all carriers (read-only endpoint)
     const backendResponse = await axios.get(`${API_BASE_URL}/admin/carrier-backends/view`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     
     // Group backends by carrier_id
     const backendsByCarrier = {};
     (backendResponse.data.mappings || []).forEach(mapping => {
       if (!backendsByCarrier[mapping.carrier_id]) {
         backendsByCarrier[mapping.carrier_id] = [];
       }
       backendsByCarrier[mapping.carrier_id].push(mapping);
     });
     setCarrierBackends(backendsByCarrier);
   } catch (err) {
     setError('Failed to fetch carriers');
     console.error('Error fetching carriers:', err);
   } finally {
     setLoading(false);
   }
 }, [API_BASE_URL]); // Dependencies: API_BASE_URL, setters

 // Memoized function to fetch user types
 const fetchUserTypes = useCallback(async () => {
   try {
     const token = localStorage.getItem('authToken');
     const response = await axios.get(`${API_BASE_URL}/admin/user-types`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setUserTypes(response.data.user_types || []);
   } catch (err) {
     console.error('Error fetching user types:', err);
     setError('Failed to fetch user types');
   }
 }, [API_BASE_URL]); // Dependencies: API_BASE_URL, setUserTypes

 // Memoized function to fetch backends
 const fetchBackends = useCallback(async () => {
   try {
     const token = localStorage.getItem('authToken');
     const response = await axios.get(`${API_BASE_URL}/admin/backends`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setBackends(response.data.backends || []);
   } catch (err) {
     console.error('Error fetching backends:', err);
   }
 }, [API_BASE_URL]);

 // Effect to fetch carriers on component mount
 useEffect(() => {
   fetchCarriers();
 }, [fetchCarriers]);

 const handleCreateCarrier = async () => {
   try {
     const token = localStorage.getItem('authToken');
     
     if (!newCarrierData.carrier_name) {
       setError('Carrier name is required');
       return;
     }

     const response = await axios.post(`${API_BASE_URL}/admin/carriers`, newCarrierData, {
       headers: {
         'Authorization': `Bearer ${token}`,
         'Content-Type': 'application/json'
       }
     });

     if (response.data.success) {
       setSuccessMessage('Carrier created successfully!');
       setShowCreateCarrierModal(false);
       setNewCarrierData({
         carrier_name: '',
         backend_id: '',
         is_active: 1
       });
       await fetchCarriers();
       setTimeout(() => setSuccessMessage(null), 3000);
     }
   } catch (err) {
     console.error('Error creating carrier:', err);
     setError(err.response?.data?.error || 'Failed to create carrier');
     setTimeout(() => setError(null), 5000);
   }
 };

 const openCreateCarrierModal = () => {
   fetchBackends();
   setShowCreateCarrierModal(true);
 };

 // Check permissions after hooks are declared
 if (!hasPermission('system.admin')) {
   return (
     <div className="flex items-center justify-center h-full">
       <div className="text-center p-12 bg-white rounded-lg shadow-sm">
         <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
         <p className="text-gray-600">You don't have permission to access this page.</p>
       </div>
     </div>
   );
 }

 const handleActionSelect = (action, carrier) => {
   setSelectedCarrier(carrier);
   if (action === 'toggle_active') {
     setPendingAction({
       type: 'toggle_active',
       newValue: !carrier.is_active
     });
     setShowConfirmDialog(true);
   } else if (action === 'edit_name') {
     setEditingCarrierName(carrier.carrier_name);
     setPendingAction({
       type: 'edit_name'
     });
     setShowConfirmDialog(true);
   } else if (action === 'create_user') {
     // Set the carrier ID for the new user
     setCreateUserData(prev => ({
       ...prev,
       carrier_id: carrier.carrier_id
     }));
     fetchUserTypes();
     setShowCreateUserModal(true);
   }
 };

 const executeAction = async () => {
   if (!selectedCarrier || !pendingAction) return;

   try {
     const token = localStorage.getItem('authToken');
     
     if (pendingAction.type === 'toggle_active') {
       await axios.put(`${API_BASE_URL}/admin/carriers/${selectedCarrier.carrier_id}`, {
         is_active: pendingAction.newValue
       }, {
         headers: {
           'Authorization': `Bearer ${token}`
         }
       });
     } else if (pendingAction.type === 'edit_name') {
       await axios.put(`${API_BASE_URL}/admin/carriers/${selectedCarrier.carrier_id}`, {
         carrier_name: editingCarrierName
       }, {
         headers: {
           'Authorization': `Bearer ${token}`
         }
       });
     }

     // Refresh the carriers list
     await fetchCarriers();
     
     // Reset state
     setShowConfirmDialog(false);
     setSelectedCarrier(null);
     setPendingAction(null);
     setEditingCarrierName('');
   } catch (err) {
     setError('Failed to update carrier');
     console.error('Error updating carrier:', err);
   }
 };

 const cancelAction = () => {
   setShowConfirmDialog(false);
   setSelectedCarrier(null);
   setPendingAction(null);
   setEditingCarrierName('');
 };

 const handleCreateUser = async () => {
     try {
       const token = localStorage.getItem('authToken');
       
       // Validate required fields
       const requiredFields = ['email', 'first_name', 'last_name', 'user_type_id'];
       for (const field of requiredFields) {
         if (!createUserData[field]) {
           setError(`${field.replace('_', ' ')} is required`);
           return;
         }
       }

       // Additional validation for local auth
       if (createUserData.auth_method === 'local') {
         if (!createUserData.username) {
           setError('Username is required for local authentication');
           return;
         }
         if (!createUserData.password || createUserData.password.length < 8) {
           setError('Password must be at least 8 characters for local authentication');
           return;
         }
       }

       const response = await axios.post(`${API_BASE_URL}/admin/users`, createUserData, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       if (response.data.success) {
         setShowCreateUserModal(false);
         setCreateUserData({
           carrier_id: '',
           user_type_id: '',
           auth_method: 'google_oauth',
           email: '',
           first_name: '',
           last_name: '',
           display_name: '',
           username: '',
           password: '',
           oauth_provider_id: ''
         });
         setError(null);
         alert('User created successfully!');
       }
     } catch (err) {
       console.error('Error creating user:', err);
       setError(err.response?.data?.error || 'Failed to create user');
     }
   };

 const handleCreateUserInputChange = (field, value) => {
   setCreateUserData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const cancelCreateUser = () => {
   setShowCreateUserModal(false);
   setCreateUserData({
     carrier_id: '',
     user_type_id: '',
     auth_method: 'google_oauth',
     email: '',
     first_name: '',
     last_name: '',
     display_name: '',
     username: '',
     password: '',
     oauth_provider_id: ''
   });
 };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading carriers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Error</h2>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchCarriers}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Carrier Management</h1>
          {(hasPermission('carrier.create') || hasPermission('system.admin')) && (
            <button
              onClick={openCreateCarrierModal}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              + New Carrier
            </button>
          )}
        </div>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Backend(s)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {carriers.map((carrier) => (
                <tr key={carrier.carrier_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {carrier.carrier_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {carrier.carrier_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {carrier.carrier_key}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {carrierBackends[carrier.carrier_id] && carrierBackends[carrier.carrier_id].length > 0 ? (
                      <div className="space-y-1">
                        {carrierBackends[carrier.carrier_id].map((mapping, idx) => (
                          <div key={idx} className="text-xs">
                            <span className="font-medium">{mapping.backend_name}</span>
                            <span className="text-gray-400"> ({mapping.host})</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs italic">No backend assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      carrier.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {carrier.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(carrier.created_date * 1000).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <select 
                      className="border border-gray-300 rounded px-3 py-1 text-sm"
                      onChange={(e) => {
                        if (e.target.value) {
                          handleActionSelect(e.target.value, carrier);
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="">Select Action</option>
                      <option value="toggle_active">
                        {carrier.is_active ? 'Deactivate' : 'Activate'}
                      </option>
                      <option value="edit_name">Edit Name</option>
                      <option value="create_user">Create New User</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {carriers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No carriers found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            
            {pendingAction?.type === 'toggle_active' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to {pendingAction.newValue ? 'activate' : 'deactivate'} 
                the carrier "{selectedCarrier?.carrier_name}"?
              </p>
            )}
            
            {pendingAction?.type === 'edit_name' && (
              <div className="mb-6">
                <p className="text-gray-600 mb-3">
                  Edit the name for carrier "{selectedCarrier?.carrier_name}":
                </p>
                <input
                  type="text"
                  value={editingCarrierName}
                  onChange={(e) => setEditingCarrierName(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Carrier name"
                />
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={pendingAction?.type === 'edit_name' && !editingCarrierName.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Carrier Modal */}
      {showCreateCarrierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New Carrier</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Carrier Name *
                </label>
                <input
                  type="text"
                  value={newCarrierData.carrier_name}
                  onChange={(e) => setNewCarrierData({...newCarrierData, carrier_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., Rogers Cable"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backend (Optional)
                </label>
                <select
                  value={newCarrierData.backend_id}
                  onChange={(e) => setNewCarrierData({...newCarrierData, backend_id: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">No backend (assign later)</option>
                  {backends.map((backend) => (
                    <option key={backend.backend_id} value={backend.backend_id}>
                      {backend.backend_name} ({backend.host})
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  You can assign or change the backend later from the Backends page.
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newCarrierData.is_active === 1}
                    onChange={(e) => setNewCarrierData({...newCarrierData, is_active: e.target.checked ? 1 : 0})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateCarrierModal(false);
                  setNewCarrierData({
                    carrier_name: '',
                    backend_id: '',
                    is_active: 1
                  });
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCarrier}
                disabled={!newCarrierData.carrier_name}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Create Carrier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New User for {selectedCarrier?.carrier_name}</h3>
            
            <div className="space-y-4">
              {/* Authentication Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authentication Method
                </label>
                <select
                  value={createUserData.auth_method}
                  onChange={(e) => handleCreateUserInputChange('auth_method', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="google_oauth">Google OAuth</option>
                  <option value="local">Username/Password</option>
                </select>
              </div>

              {/* User Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Type *
                </label>
                <select
                  value={createUserData.user_type_id}
                  onChange={(e) => handleCreateUserInputChange('user_type_id', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select User Type</option>
                  {userTypes.map((type) => (
                    <option key={type.user_type_id} value={type.user_type_id}>
                      {type.type_name} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={createUserData.email}
                  onChange={(e) => handleCreateUserInputChange('email', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="user@example.com"
                  required
                />
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={createUserData.first_name}
                  onChange={(e) => handleCreateUserInputChange('first_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="John"
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={createUserData.last_name}
                  onChange={(e) => handleCreateUserInputChange('last_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Doe"
                  required
                />
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={createUserData.display_name}
                  onChange={(e) => handleCreateUserInputChange('display_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Leave empty to use First + Last Name"
                />
              </div>

              {/* Username (only for local auth) */}
              {createUserData.auth_method === 'local' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={createUserData.username}
                    onChange={(e) => handleCreateUserInputChange('username', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="username"
                    required
                  />
                </div>
              )}

              {/* Password (only for local auth) */}
              {createUserData.auth_method === 'local' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={createUserData.password}
                    onChange={(e) => handleCreateUserInputChange('password', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              )}

              {/* OAuth Provider ID (only for OAuth) */}
              {createUserData.auth_method === 'google_oauth' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google OAuth Provider ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={createUserData.oauth_provider_id}
                    onChange={(e) => handleCreateUserInputChange('oauth_provider_id', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Will be set automatically on first login if left empty"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Leave empty to allow the system to set this automatically when the user first logs in with Google.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cancelCreateUser}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCarriersPage;
